import {
  settingsWords as words,
  settingsText,
} from "@/components/settings/settings-copy";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { Bento, PageRefusal } from "@/components/ui/box";
import type {
  Box, Conflict, FieldSave, FieldAria,
} from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import {
  getStudentMe,
  patchStudentMe,
  type SelfStudentProfile,
} from "@/lib/api/studentSelf";
import { boxRead, imam } from "@/components/settings/settings-boxes";
import { settingsCopy as copy } from "@/components/settings/settings-copy";

const fields = [
  {
    key: "preferred_name",
    label: words.preferredName,
    note: words.yourInstructorsAndTheRegistrarCanSeeThisName,
  },
  {
    key: "phone",
    label: words.phone,
    note: words.theRegistrarCanSeeThisPhoneNumber,
  },
  {
    key: "address",
    label: words.address,
    note: words.theRegistrarCanSeeThisAddress,
  },
] as const;
type Field = (typeof fields)[number]["key"];
type Draft = Record<Field, string>;
const draftOf = (profile: SelfStudentProfile): Draft => ({
  preferred_name: profile.preferred_name ?? "",
  phone: profile.phone ?? "",
  address: profile.address ?? "",
});

export function ProfilePage() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["student", "me"],
    queryFn: getStudentMe,
    retry: false,
  });
  const base = useRef<SelfStudentProfile | null>(null);
  const formRef = useRef<Draft>({ preferred_name: "", phone: "", address: "" });
  const [form, setForm] = useState<Draft>(formRef.current);
  const [saves, setSaves] = useState<Partial<Record<Field, FieldSave>>>({});
  const [conflicts, setConflicts] = useState<
    Partial<Record<Field, SelfStudentProfile>>
  >({});
  const conflictRef = useRef<Partial<Record<Field, SelfStudentProfile>>>({});
  const [checking, setChecking] = useState<Partial<Record<Field, string>>>({});
  const checkingRef = useRef<Partial<Record<Field, string>>>({});
  const chain = useRef<Promise<void>>(Promise.resolve());
  const locked = useRef<Partial<Record<Field, boolean>>>({});
  const setField = (field: Field, value: string) => {
    formRef.current = { ...formRef.current, [field]: value };
    setForm(formRef.current);
  };
  useEffect(() => {
    if (query.data && base.current?.id !== query.data.id) {
      base.current = query.data;
      formRef.current = draftOf(query.data);
      setForm(formRef.current);
    }
  }, [query.data]);
  const status = (field: Field, save: FieldSave) =>
    setSaves((current) => ({ ...current, [field]: save }));
  const compare = (field: Field, next?: SelfStudentProfile) => {
    conflictRef.current = { ...conflictRef.current, [field]: next };
    setConflicts(conflictRef.current);
    locked.current[field] = Boolean(next);
  };
  // Advancing the guard and the untouched displayed values is one operation.
  // Otherwise a later blur mistakes stale text for a deliberate edit.
  const adopt = (next: SelfStudentProfile, chosen?: { field: Field; value: string }) => {
    const before = base.current;
    const draft = formRef.current;
    const rebased = draftOf(next);
    for (const { key } of fields) {
      const drafted = draft[key] !== (before?.[key] ?? "");
      if (key !== chosen?.field && !checkingRef.current[key] && (conflictRef.current[key] ||
        (drafted && rebased[key] !== (before?.[key] ?? "") && rebased[key] !== draft[key]))) {
        compare(key, next);
        // The notice owns this comparison; don't announce its sentence twice.
        status(key, { status: "idle" });
      }
      if (drafted) rebased[key] = draft[key];
    }
    if (chosen) rebased[chosen.field] = chosen.value;
    base.current = next;
    formRef.current = rebased;
    setForm(rebased);
    client.setQueryData(["student", "me"], next);
  };
  const send = (field: Field) => {
    if (locked.current[field]) return;
    locked.current[field] = true;
    chain.current = chain.current.then(async () => {
      // A prior queued save may have discovered an independent change here.
      if (conflictRef.current[field]) return;
      const before = base.current;
      const value = formRef.current[field];
      if (!before || value === (before[field] ?? "")) {
        locked.current[field] = false;
        return;
      }
      const key = crypto.randomUUID();
      status(field, { status: "saving" });
      try {
        const saved = await patchStudentMe(
          {
            [field]: value,
            expected_updated_at: before.updated_at,
            expected_person_updated_at: before.person_updated_at,
          },
          key,
        );
        adopt(saved, formRef.current[field] === value
          ? { field, value: saved[field] ?? "" }
          : undefined);
        locked.current[field] = false;
        status(field, { status: "saved" });
      } catch (error) {
        const newer = currentOnConflict<SelfStudentProfile>(error);
        if (newer) {
          compare(field, newer);
          status(field, {
            status: "refused",
            sentence: reasonOnError(error)?.message ?? (error instanceof Error ? error.message : words.thisFieldChangedSinceYouReadItYourTypedValueIsKept),
          });
        } else if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          locked.current[field] = false;
          status(field, {
            status: "refused",
            sentence: reasonOnError(error)?.message ?? error.message,
          });
        } else {
          checkingRef.current = { ...checkingRef.current, [field]: key };
          setChecking(checkingRef.current);
          status(field, {
            status: "unknown",
            check: () => void check(field, key),
          });
        }
      }
    });
  };
  const check = async (field: Field, key: string) => {
    try {
      const result = await checkCommit(key);
      if (result.state !== "confirmed" && result.state !== "not_committed")
        return;
      if (result.state === "confirmed") {
        const fresh = await getStudentMe();
        adopt(fresh, { field, value: fresh[field] ?? "" });
        status(field, { status: "saved" });
      } else
        status(field, {
          status: "failed",
          sentence: words.thisValueWasNotSavedTryAgain,
          retry: () => send(field),
        });
      locked.current[field] = false;
      checkingRef.current = { ...checkingRef.current, [field]: undefined };
      setChecking(checkingRef.current);
    } catch {
      status(field, {
        status: "unknown",
        check: () => void check(field, key),
      });
    }
  };
  const read = boxRead(query, copy.recordUnavailable);
  const missing = query.error instanceof ApiError && query.error.status === 404;
  const resolvedRead = missing
    ? {
        status: "empty" as const,
        sentence: words.noStudentRecordOnFileTheRegistrarCreatesIt,
      }
    : read;
  const boxes: Box[] = [
    {
      id: "my-record",
      kind: "fact",
      job: words.readYourOwnAcademicRecord,
      label: copy.record,
      owner: imam,
      read: resolvedRead,
      fold: "never",
      detail: () => words.theRegistrarMaintainsYourIdentityAndAcademicPlacement,
      prose: (raw) =>
        settingsText.studentRecordAnswer((raw as SelfStudentProfile).sid),
      body: (raw) => {
        const profile = raw as SelfStudentProfile;
        return (
          <dl className="grid gap-3 sm:grid-cols-2">
            {[
              [words.honorific, profile.honorific],
              [words.legalName, profile.legal_name],
              [words.program, profile.program_name],
              [words.cohort, profile.cohort_name],
              [words.status, settingsText.studentStatus(profile.status)],
              [words.email, profile.email],
              [words.city, profile.city],
              [words.country, profile.country],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-meta text-muted-foreground">{label}</dt>
                <dd className="break-words text-ui">
                  {value || words.notRecorded}
                </dd>
              </div>
            ))}
          </dl>
        );
      },
    },
    {
      id: "contact-details",
      kind: "fact",
      job: words.maintainYourOwnContactDetails,
      label: copy.contact,
      owner: imam,
      read: missing
        ? { status: "empty", sentence: copy.contactMissing }
        : boxRead(query, copy.contactUnavailable),
      fold: "never",
      detail: () => words.editEachContactDetailWhereItIsShown,
      fields: () =>
        fields.map(({ key, label, note }) => {
          const newer = conflicts[key];
          const conflict: Conflict | undefined = newer
            ? {
                sentence:
                  words.thisFieldChangedSinceYouReadItYourTypedValueIsKept,
                facts: [{ label, theirs: newer[key] || words.notRecorded }],
                keepMine: {
                  label: words.keepMine,
                  run: () => {
                    locked.current[key] = false;
                    adopt(newer, { field: key, value: formRef.current[key] });
                    compare(key);
                    status(key, { status: "idle" });
                    document
                      .querySelector<HTMLInputElement>(
                        `[data-field="${key}"] input`,
                      )
                      ?.focus();
                  },
                },
                loadNewer: {
                  label: words.loadTheirs,
                  run: () => {
                    locked.current[key] = false;
                    adopt(newer, { field: key, value: newer[key] ?? "" });
                    compare(key);
                    status(key, { status: "idle" });
                    document
                      .querySelector<HTMLInputElement>(
                        `[data-field="${key}"] input`,
                      )
                      ?.focus();
                  },
                },
              }
            : undefined;
          const unsettled = Boolean(checking[key]) || saves[key]?.status === "saving";
          return {
            id: key,
            label,
            note,
            conflict,
            save: saves[key] ?? { status: "idle" },
            render: (labelledBy: string, aria: FieldAria) => (
              <div className="grid gap-2">
                <Input
                  aria-labelledby={labelledBy}
                  value={form[key]}
                  type={key === "phone" ? "tel" : "text"}
                  {...aria}
                  readOnly={unsettled}
                  onChange={(event) => {
                    if (unsettled) return;
                    setField(key, event.target.value);
                    if (!conflicts[key]) status(key, { status: "idle" });
                  }}
                  onBlur={() => send(key)}
                  onKeyDown={(event) => {
                    if (unsettled) return;
                    if (event.key === "Enter") {
                      event.preventDefault();
                      send(key);
                    }
                    if (event.key === "Escape") {
                      event.preventDefault();
                      if (base.current) setField(key, base.current[key] ?? "");
                      if (!conflicts[key]) status(key, { status: "idle" });
                    }
                  }}
                />
              </div>
            ),
          };
        }),
    },
  ];
  if (query.error instanceof ApiError && query.error.status === 403)
    return (
      <PageRefusal
        id="profile"
        journey="profile"
        refusal={{
          reason: "forbidden",
          sentence: copy.forbidden,
          back: { label: words.goBack, to: "/student" },
        }}
      />
    );
  return (
    <div className="app-page">
      <AcademicPageHeader
        eyebrow={words.studentAccount}
        title={copy.profile}
        description={
          words.yourAcademicRecordAndTheContactDetailsYouMaintainYourself
        }
      />
      <Bento id="profile" journey="profile" boxes={boxes.map((box) => ({ ...box, readTime: true as const }))} />
    </div>
  );
}
